import { CSSProperties, useEffect, useState } from 'react'
import Colors from "modules/Colors"
import { ReactComponent as CheckGreen } from "assets/images/check-green.svg"
import useScreen from 'Hooks/useScreen'
import { BillingCycle } from './shareable/UpgradeToProModal/utils'
import {
    PlanType,
    DEFAULT_CURRENCY,
    SUBSCRIPTION_BENEFITS,
    Perk
} from 'modules/Const'
import useBrand from 'Hooks/useBrand'
import { track } from 'analytics'
import {
    changeSubscriptionPlan_api,
    ChangeSubscriptionPlanParams,
    DiscountDetails,
    upgradePlanAndRedirectToCheckout_api,
    UpgradePlanAndRedirectToCheckoutParams
} from 'api/subscriptionApi'
import { getConvertedCurrency_api } from "api/campaignApi"
import { getNumberWithDecimals } from "helpers/General"
import getSymbolFromCurrency from 'currency-symbol-map'
import { formatNumberToCurrency, styx } from "modules/Utils"
import { toastError } from 'modules/Toast'
import { useCancellation } from 'Hooks/useCancellation'
import useUser from 'Hooks/useUser'
import useOrganization from 'Hooks/useOrganization'
import ButtonSymphony from '../elements/ButtonSymphony'
import { PaymentMethods } from 'modules/Types'
import Icon from 'elements/Icon'

interface SubscriptionDetailsTileProps {
    /** Type of subscription plan to display */
    subscriptionType: PlanType;
    /** Flag to show if this is the most popular plan */
    isMostPopular?: boolean;
    /** Selected billing cycle (monthly/annual) */
    billingCycle?: BillingCycle;
    /** Flag to highlight this subscription tile */
    isHighlighted?: boolean;
    /** Source of the checkout */
    checkout_source?: string;
    /** Styles for the tile */
    style?: CSSProperties;
    /** Callback function to be called when a user upgrades To Pro or downgrades to Lite successfully */
    onPlanChangedSuccess?: (tier: PlanType) => void;
    /** Whether this is a downgrade flow */
    isDowngrade?: boolean;
    /** Callback function to update payment method */
    onUpdatePaymentMethod?: () => void;
    /** Flag to show if the update payment method button should be shown */
    showUpdatePaymentMethod?: boolean;
    /** URL to redirect to after the subscription is updated */
    successUrl?: string;
    /** URL to redirect to after the subscription is updated */
    cancelUrl?: string;
}

const SubscriptionDetailsTile = ({
    subscriptionType = PlanType.LITE,
    isMostPopular = false,
    billingCycle = BillingCycle.MONTHLY,
    isHighlighted: isHighlightedProp = false,
    checkout_source = '',
    style = {},
    onPlanChangedSuccess,
    isDowngrade = false,
    onUpdatePaymentMethod,
    showUpdatePaymentMethod = false,
    successUrl,
    cancelUrl,
}: SubscriptionDetailsTileProps) => {
    const { mobileView } = useScreen()
    const { proFreeTrialRedeemed, liteFreeTrialRedeemed } = useUser()
    const { handleCancellationFlow, cancellationButtonId, isCancelling } = useCancellation();

    const {
        currentBrand,
        isPaidBrand,
        isLiteBrand,
        isProBrand,
        getBrand,
        getAffiliatePartnerDetails,
        calculatePartnerDiscountAmount
    } = useBrand()
    const { goToTeamCheckout, teamFreeTrialRedeemed } = useOrganization();

    const [loadingCheckout, setLoadingCheckout] = useState(false)
    const [loadingUpdatePaymentMethod, setLoadingUpdatePaymentMethod] = useState(false)
    const [discountDetails, setDiscountDetails] = useState<DiscountDetails | null>(null)

    const fetchDiscountDetails = async () => {
        const discountDetails = await calculatePartnerDiscountAmount(subscriptionType, billingCycle)
        setDiscountDetails(discountDetails)
    }

    useEffect(() => {
        fetchDiscountDetails()
    }, [])
    /** Current currency code for pricing display */
    const currencyCode = currentBrand?.currency?.code || DEFAULT_CURRENCY

    /** Current tier of the brand */
    const currentTier = currentBrand?.tier

    /** Partner discount amount if applicable */
    const calculateDiscountAmount = (): number => {
        if (!discountDetails) {
            return 0;
        }

        const { planDiscounts } = discountDetails.partnerDetails || {};
        if (!planDiscounts) {
            return 0;
        }

        const planDiscount = planDiscounts[subscriptionType as PlanType.PRO | PlanType.LITE];
        if (!planDiscount) {
            return 0;
        }
        return planDiscount[billingCycle] || 0;
    }

    let hasDiscount = !!discountDetails?.discountId;
    let discountAmount = calculateDiscountAmount();

    /** Gets the base USD price for a subscription plan */
    const getUsdPrice = (planType: PlanType, cycle: BillingCycle): number => {
        return cycle === BillingCycle.MONTHLY
            ? SUBSCRIPTION_BENEFITS[planType].priceMonthly
            : SUBSCRIPTION_BENEFITS[planType].priceAnnual
    }

    /** Base price in USD */
    const usdPrice = getUsdPrice(subscriptionType, billingCycle)

    /** State for storing the converted price in user's currency */
    const [userFacingPrice, setUserFacingPrice] = useState<number>(usdPrice)

    /**
     * Converts USD price to user's selected currency
     */
    const convertToNewCurrencyPrice = async (): Promise<void> => {
        if (currencyCode !== DEFAULT_CURRENCY) {
            const res = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: usdPrice })
            setUserFacingPrice(Number(getNumberWithDecimals(res, 2)))
        } else {
            setUserFacingPrice(usdPrice)
        }
    }

    /**
     * Handles payment method update request
     */
    const handleUpdatePaymentMethod = async (): Promise<void> => {
        if (!onUpdatePaymentMethod) {
            return
        }

        try {
            setLoadingUpdatePaymentMethod(true)
            await onUpdatePaymentMethod()
        } catch (error) {
            console.error('Error updating payment method:', error)
        } finally {
            setLoadingUpdatePaymentMethod(false)
        }
    }

    useEffect(() => {
        convertToNewCurrencyPrice()
    }, [currencyCode, billingCycle])

    /**
     * Determines if a plan change between paid plans is occurring
     * Used to decide whether to call changeSubscriptionPlan_api
     */
    const isPlanChangeBetweenPaidPlans = (): boolean => {
        // Only applies to changes between Pro and Lite plans (in either direction)
        return isPaidBrand &&
            currentTier !== subscriptionType &&
            (subscriptionType === PlanType.PRO || subscriptionType === PlanType.LITE) &&
            (currentTier === PlanType.PRO || currentTier === PlanType.LITE);
    }

    /**
     * Handles direct plan change between paid plans (Pro <-> Lite)
     */
    const handlePlanChangeBetweenPaidPlans = async (): Promise<boolean> => {
        // Prepare subscription change data
        const data: ChangeSubscriptionPlanParams = {
            brandSlug: currentBrand?.slug || '',
            recurring_interval: billingCycle,
            checkout_source: checkout_source || '',
            plan_type: subscriptionType
        };

        track("Subscription Plan Change Started");
        const result = await changeSubscriptionPlan_api(data);

        // Handle successful plan change
        if (result.success) {
            await getBrand()
            if (onPlanChangedSuccess) {
                onPlanChangedSuccess(result.tier as PlanType)
            }
            return true;
        }
        return false;
    }

    /**
     * Handles subscription button click for both upgrade and downgrade flows
     * 
     * Plan Change Behavior:
     * - Changes between Pro and Lite: Use changeSubscriptionPlan_api
     * - Downgrade to Free plan: Use cancellation flow
     * - Upgrade to Team plan: Redirect to team checkout
     * - Other upgrades: Redirect to standard checkout
     */
    const handleUpgradeOrDowngrade = async (): Promise<void> => {
        setLoadingCheckout(true);
        try {
            // Handle changes between paid plans (Pro <-> Lite) regardless of upgrade/downgrade direction
            if (isPlanChangeBetweenPaidPlans()) {
                await handlePlanChangeBetweenPaidPlans();
            }
            // Handle downgrade to Free plan (cancellation)
            else if (isDowngrade && isPaidBrand && subscriptionType === PlanType.FREE) {
                await handleCancellationFlow();
            }
            // Handle upgrade to Team plan
            else if (!isDowngrade && subscriptionType === PlanType.TEAM) {
                track("Start Subscription Checkout", {
                    planType: subscriptionType,
                    billingCycle: billingCycle,
                    hasFreeTrial: teamFreeTrialRedeemed()
                });

                await goToTeamCheckout({
                    recurring_interval: billingCycle,
                    brandSlug: currentBrand?.slug
                });
            }
            // Handle other plan upgrades (Free -> Lite/Pro)
            else if (!isDowngrade) {
                // Determine trial eligibility
                const isTrial = subscriptionType === PlanType.LITE
                    ? !liteFreeTrialRedeemed()
                    : !proFreeTrialRedeemed();

                track("Start Subscription Checkout", {
                    planType: subscriptionType,
                    billingCycle: billingCycle,
                    hasFreeTrial: isTrial
                });

                // Prepare upgrade data
                const data: UpgradePlanAndRedirectToCheckoutParams = {
                    recurring_interval: billingCycle,
                    plan_type: subscriptionType,
                    brandSlug: currentBrand?.slug || '',
                    payment_method: PaymentMethods.STRIPE,
                    ...(checkout_source ? { checkout_source: 'onboarding-signup' as 'onboarding-signup' } : {}),
                    successUrl,
                    cancelUrl,
                };

                // Redirect to standard checkout
                await upgradePlanAndRedirectToCheckout_api(data);
            }
        } catch (error) {
            toastError("There was an error processing your subscription.");
        } finally {
            setLoadingCheckout(false);
        }
    }

    const isFreeTrialEligible = (): boolean => {
        if (subscriptionType === PlanType.PRO) {
            return !proFreeTrialRedeemed();
        } else if (subscriptionType === PlanType.LITE) {
            return !liteFreeTrialRedeemed();
        } else if (subscriptionType === PlanType.TEAM) {
            return !teamFreeTrialRedeemed();
        }
        return false;
    }

    const getButtonText = (): string => {
        // Check if current subscription matches the tile's subscription type
        const isCurrentPlan = currentTier === subscriptionType;

        // If this is the current plan, always show "Your Current Plan"
        if (isCurrentPlan) {
            return "Your Current Plan";
        }

        // Get default button text based on trial eligibility
        const freeTrialEligible = isFreeTrialEligible();
        let defaultText = freeTrialEligible
            ? SUBSCRIPTION_BENEFITS[subscriptionType].buttonFreeTrialText
            : SUBSCRIPTION_BENEFITS[subscriptionType].buttonText;

        // Handle specific cases for downgrade flow
        if (isDowngrade) {
            if (subscriptionType === PlanType.FREE && (isLiteBrand || isProBrand)) {
                return 'Cancel Subscription';
            }
        }

        return defaultText;
    }

    const renderActionButton = () => {
        // Check if current subscription matches the tile's subscription type
        const isCurrentPlan = currentTier === subscriptionType;

        // For cancellation (downgrade to free)
        if (isDowngrade && subscriptionType === PlanType.FREE && currentTier !== PlanType.FREE) {
            return (
                <>
                    <ButtonSymphony
                        className="cancelSubscriptionDetailsButton"
                        id={cancellationButtonId}
                        data-cancel-button={true}
                        disabled={isCancelling}
                        isLoading={isCancelling}
                        width="100%"
                        onClick={() => {
                            // If there is no cancellation button id, we need to handle the cancellation flow directly
                            if (!cancellationButtonId) {
                                handleCancellationFlow();
                            }
                        }}
                    >
                        Cancel Subscription
                    </ButtonSymphony>
                    <p style={sty.buttonDisclaimer}>Your subscription will be downgraded to free immediately upon cancellation.</p>
                </>
            );
        }

        // For payment method update
        if (showUpdatePaymentMethod && isCurrentPlan) {
            return (
                <ButtonSymphony
                    className="currentPlanSubscriptionButton"
                    isLoading={loadingUpdatePaymentMethod}
                    width="100%"
                    onClick={handleUpdatePaymentMethod}
                >
                    Update Payment Method
                </ButtonSymphony>
            );
        }

        // Standard subscription button (disabled if current plan)
        return (
            <ButtonSymphony
                className={isCurrentPlan ? 'currentPlanSubscriptionButton' : 'notCurrentPlanSubscriptionButton'}
                isLoading={loadingCheckout}
                disabled={loadingCheckout || isCurrentPlan}
                width="100%"
                onClick={handleUpgradeOrDowngrade}
            >
                {getButtonText()}
            </ButtonSymphony>
        );
    }

    const renderTitle = () => {
        return (
            <div className="tileTitle" style={sty.tileTitle}>
                {renderSubscriptionIcon()}
                {SUBSCRIPTION_BENEFITS[subscriptionType].title}
            </div>
        )
    }

    const getPriceDetails = () => {
        const currencySymbol = getSymbolFromCurrency(currencyCode)

        // Calculate monthly prices
        const monthlyBase = billingCycle === BillingCycle.MONTHLY ? userFacingPrice : userFacingPrice / 12
        const monthlyDiscounted = discountAmount ? monthlyBase * discountAmount : monthlyBase

        // Calculate annual prices
        const annualBase = userFacingPrice
        const annualDiscounted = discountAmount ? annualBase * discountAmount : annualBase

        return {
            monthly: {
                original: formatNumberToCurrency(monthlyBase, true),
                discounted: formatNumberToCurrency(monthlyDiscounted, true),
                hasDiscount: !!discountAmount
            },
            annual: {
                original: formatNumberToCurrency(annualBase, true),
                discounted: formatNumberToCurrency(annualDiscounted, true),
                hasDiscount: !!discountAmount
            },
            currencySymbol
        }
    }

    const renderPrice = () => {
        const prices = getPriceDetails()
        const isAnnual = billingCycle === BillingCycle.ANNUAL
        const currentPrices = isAnnual ? prices.annual : prices.monthly

        // Calculate the display price for annual billing with proper decimal handling
        const displayPrice = isAnnual ?
            formatNumberToCurrency(Number(currentPrices.discounted) / 12, true) :
            currentPrices.discounted

        // Calculate the original display price for annual billing with proper decimal handling
        const displayOriginalPrice = isAnnual && currentPrices.hasDiscount ?
            formatNumberToCurrency(Number(currentPrices.original) / 12, true) :
            currentPrices.original

        return (
            <>
                <div className="tilePriceContainer" style={sty.tilePriceContainer}>
                    <div className="tilePriceWrapper" style={sty.tilePriceWrapper}>
                        {currentPrices.hasDiscount &&
                            <div style={sty.originalPrice}>
                                {prices.currencySymbol}{displayOriginalPrice}
                            </div>
                        }
                        <div className="tilePrice" style={sty.tilePrice}>
                            {prices.currencySymbol}{displayPrice}
                        </div>
                    </div>
                    <div className="tilePricePer" style={sty.tilePricePer}>
                        / month
                    </div>
                </div>

                {isAnnual &&
                    <div className="tilePriceAnnual" style={sty.tilePriceAnnual}>
                        <div>{prices.currencySymbol}{currentPrices.discounted}</div>
                        <div style={sty.billingNote}>
                            / year billed upfront
                        </div>
                    </div>
                }
            </>
        )
    }

    const getPartnerDiscountText = (): string | null => {
        const partnerDetails = getAffiliatePartnerDetails()
        const partnerId = partnerDetails?.partnerId
        const partnerName = partnerDetails?.partnerName

        if (!hasDiscount || !partnerId) {
            return null;
        }

        const discountPercentage = (1 - discountAmount) * 100
        const basePartnerLabel = `${discountPercentage}% off ${subscriptionType.toUpperCase()} for 12 months!`
        return `${partnerName} users receive ${basePartnerLabel}`
    }

    const renderSubscriptionIcon = () => {
        switch (subscriptionType) {
            case PlanType.LITE:
                return (
                    <Icon
                        className="subscriptionIcon"
                        style={sty.subscriptionIcon}
                        icon="gem"
                        color={Colors.purple}
                        size={38}
                        isCircle
                    />
                )
            case PlanType.PRO:
                return (
                    <Icon
                        className="subscriptionIcon"
                        style={styx(
                            sty.subscriptionIcon,
                            {
                                backgroundColor: Colors.goldTransparent,
                            }
                        )}
                        icon="gem"
                        color={Colors.gold}
                        size={38}
                        isCircle
                    />
                )
            case PlanType.TEAM:
                return (
                    <Icon
                        className="subscriptionIcon"
                        style={styx(
                            sty.subscriptionIcon,
                            {
                                backgroundColor: Colors.goldTransparent,
                            }
                        )}
                        icon="gem"
                        color={Colors.gold}
                        size={38}
                        isCircle
                    />
                )
            default:
                return null;
        }
    }

    const renderSubtitle = () => {
        return (
            <div className="tileSubtitle" style={sty.tileSubtitle}>
                {SUBSCRIPTION_BENEFITS[subscriptionType].subtitle}
            </div>
        )
    }

    const renderMostPopularBanner = () => {
        const partnerText = getPartnerDiscountText()
        const showBanner = isMostPopular || !!discountAmount

        if (!showBanner) {
            return null;
        }

        return (
            <div
                className="mostPopularBanner"
                style={{
                    ...sty.mostPopularBanner,
                    ...(!!discountAmount ? sty.partnerDiscountBanner : {})
                }}
            >
                {partnerText || 'MOST POPULAR'}
            </div>
        )
    }

    const renderDetails = () => {
        const perks = SUBSCRIPTION_BENEFITS[subscriptionType].perks;

        return (
            <div className="tileDetails" style={sty.tileDetails}>
                {perks.map((perk: Perk, index: number) => {
                    const { info, detail } = perk;
                    const hasMobileStyles = mobileView ? {
                        flexDirection: 'column' as const,
                        alignItems: 'flex-start' as const
                    } : {};

                    return (
                        <div
                            key={`perk-${index}`}
                            className="perkContainer"
                            style={{
                                ...sty.perkContainer,
                                ...hasMobileStyles
                            }}
                        >
                            <div className="perkInfoContainer" style={sty.perkInfoContainer}>
                                <CheckGreen style={{ marginRight: 8, width: 18, flexShrink: 0 }} />
                                <div className="perkInfo" style={sty.perkInfo}>
                                    {info}
                                </div>
                            </div>
                            {detail && (
                                <div
                                    className="perkDetail"
                                    style={
                                        styx(
                                            sty.perkDetail,
                                            mobileView ? {
                                                marginLeft: 26,
                                                textAlign: 'left' as const
                                            } : {}
                                        )
                                    }
                                >
                                    {detail}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    // Conditionally apply styles for mobile view
    const mobileStyles = mobileView ? {
        maxWidth: '100%',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
    } : {};

    // Determine if the tile should be highlighted
    const isHighlighted = isHighlightedProp || (isDowngrade && currentTier === subscriptionType);

    return (
        <div
            className="subscriptionDetailsTileComponent"
            style={styx(
                sty.subscriptionDetailsTileComponent,
                (isHighlighted ? sty.highlightedTile : {}),
                style,
                mobileStyles
            )}
        >
            {renderMostPopularBanner()}
            <div
                className="subscriptionDetailsTileInner"
                style={sty.subscriptionDetailsTileInner}
            >
                {renderTitle()}
                {renderPrice()}
                {renderSubtitle()}
                {renderActionButton()}
            </div>

            {renderDetails()}
        </div>
    )
}

/** Component styles */
const sty: Record<string, CSSProperties> = {
    subscriptionDetailsTileComponent: {
        width: '100%',
        maxWidth: 300,
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 16,
        backgroundColor: 'white',
        boxShadow: 'none',
    },
    subscriptionDetailsTileInner: {
        paddingTop: 28,
        paddingLeft: 16,
        paddingRight: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
    },
    buttonDisclaimer: {
        fontSize: 12,
        color: Colors.greyDark,
        marginTop: 8,
        fontWeight: 400,
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
    },
    tileTitle: {
        display: 'flex',
        flexDirection: 'row',
        fontSize: 24,
        alignItems: 'flex-end',
        fontWeight: 500,
    },
    tilePriceContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
    },
    tilePriceWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    tilePrice: {
        fontSize: 40,
        fontWeight: 500,
    },
    tilePricePer: {
        alignSelf: 'center',
        color: Colors.greyDark,
    },
    tilePriceAnnual: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        fontSize: 24,
        fontWeight: 500,
        color: Colors.greyDark,
    },
    tileSubtitle: {
        fontSize: 16,
        color: Colors.greyDark,
        minHeight: 60,
    },
    mostPopularBanner: {
        textAlign: 'center',
        width: '100%',
        backgroundColor: Colors.purpleLightest,
        color: Colors.purple,
        padding: '4px 8px',
        borderTopRightRadius: 16,
        borderTopLeftRadius: 16,
        fontSize: 10,
        fontWeight: 500,
        marginBottom: -23,
    },
    partnerDiscountBanner: {
        fontSize: 12,
        backgroundColor: '#FFD56B',
        color: '#000000',
    },
    tileDetails: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 16,
    },
    perkContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    perkInfo: {
        color: Colors.greyDark,
    },
    perkInfoContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    perkDetail: {
        fontSize: 14,
        color: Colors.green,
        textAlign: 'right',
    },
    subscriptionIcon: {
        flexShrink: 0,
        marginRight: 8,
        borderRadius: 12,
        padding: 7
    },
    originalPrice: {
        textDecoration: 'line-through',
        color: Colors.greyDark,
        fontSize: 24,
    },
    billingNote: {
        color: Colors.greyDark,
        fontSize: 12
    },
    highlightedTile: {
        border: `2px solid ${Colors.purple}`,
        boxShadow: `0 0 8px rgba(99, 66, 194, 0.25)`,
    },
    currentPlanIndicator: {
        backgroundColor: Colors.purpleLightest,
        color: Colors.purple,
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        marginTop: 8,
        textAlign: 'center',
    },
}

export default SubscriptionDetailsTile